import { Dispatch } from 'redux';
import subscribeSchool from '../api/school-wishlist/subscribe-school';
import unsubscribeSchool from '../api/school-wishlist/unsubscribe-school';
import { follow, unfollow } from '../api/subscriptions/follow';
import getWishlistSummary from '../api/wishlist/get-wishlist-summary';
import API_URLS from '../constants/api-urls';
import Routes from '../constants/routes';
import { world } from '../languages';
import analytics from '../services/analytics';
import qualityEvents from '../services/quality-events/quality-events';
import invalidateSliderCaches from '../services/slider-queries/invalidate-slider-caches';
import anonymousPersist from '../utils/anonymous-persist';
import session from '../utils/session';

export const FAVORITE_GET = 'favorite/GET';

const initialState: Favorite = {
    list: [],
    loaded: false,
    total: 0
};

type FavoriteActionType = Favorite & {
    type: string;
};

export default (state = initialState, action: FavoriteActionType) => {
    switch (action.type) {
        case FAVORITE_GET:
            return {
                ...state,
                list: action.list,
                loaded: action.loaded,
                total: action.total
            };
        default:
            return state;
    }
};

const getFavByURL = (url: string, dispatch: Dispatch, type = FAVORITE_GET) => {
    getWishlistSummary(url)
        .then(({ list, total }) => {
            dispatch({
                list,
                loaded: true,
                total,
                type
            });
        })
        .catch((error) => {
            console.error(error);
        });
};

export const getFavorite = () => (dispatch: Dispatch) => {
    if (session.isLoggedIn()) {
        anonymousPersist().finally(() => {
            getFavByURL(`${API_URLS.WISHLIST}/query`, dispatch);
        });
    } else {
        getFavByURL(`${API_URLS.ANONYMOUS_WISHLIST}/query`, dispatch);
    }
};

type TrackFavoriteChangeArgs = {
    material: Material;
    materialIndex?: number;
    add: boolean;
    viaSchoolWishlist: boolean;
    schoolId: number | null;
    searchRequestId?: string;
};

const getFavoriteChangeSource = () => {
    const isFavoritesPage = window.location.pathname.indexOf(Routes.FAVORITES.path) >= 0;

    const materialPagePath = Routes.MATERIAL.path.slice(0, Routes.MATERIAL.path.indexOf(':id'));
    const isMaterialPage = window.location.pathname.indexOf(materialPagePath) > -1;

    return isMaterialPage ? 'fromMaterialPage' : isFavoritesPage ? 'fromFavorites' : undefined;
};

const trackFavoriteChange = (args: TrackFavoriteChangeArgs) => {
    const { material, materialIndex, add, viaSchoolWishlist, schoolId, searchRequestId } = args;

    if (viaSchoolWishlist) {
        if (add) {
            qualityEvents.schoolWishlist.addToSchoolWishlist({ schoolId, materialId: material.id, searchRequestId });
        } else {
            qualityEvents.schoolWishlist.removeFromSchoolWishlist({
                schoolId,
                materialId: material.id,
                searchRequestId
            });
        }

        return;
    }

    const from = getFavoriteChangeSource();

    analytics.push({
        event: 'gtmEvent',
        eventAction: add ? 'clickAddToFavorites' : 'clickRemoveFromFavorites',
        eventCategory: 'mkt-userInteraction',
        eventLabel: '',
        eventNonInteraction: false
    });

    if (add) {
        qualityEvents.favoritesPage.addToFavorites(material, materialIndex, from, searchRequestId);
    } else {
        qualityEvents.favoritesPage.removeFromFavorites(material, materialIndex, from, searchRequestId);
    }
};

type ChangeFavoriteOptions = {
    materialIndex?: number;
    worldOverride?: string;
    viaSchoolWishlist?: boolean;
    isApproved?: boolean;
    searchRequestId?: string;
};

const requestSchoolWishlistChange = (materialId: number, add: boolean, isApproved?: boolean) =>
    add ? subscribeSchool({ materialId }) : unsubscribeSchool({ materialId, isApproved });

export const changeFavorite =
    (material: Material, add: boolean, options: ChangeFavoriteOptions = {}) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const { materialIndex, worldOverride, viaSchoolWishlist = false, isApproved, searchRequestId } = options;

        const favoriteAction = add ? follow : unfollow;

        const requestConfig = worldOverride ? { params: { world: worldOverride } } : undefined;

        const request = viaSchoolWishlist
            ? requestSchoolWishlistChange(material.id, add, isApproved)
            : favoriteAction(
                  {
                      subjectId: material.id,
                      subjectType: 'material',
                      world: worldOverride ?? world
                  },
                  !session.isLoggedIn(),
                  requestConfig
              );

        const favouriteMaterials: Favorite = getState().main.favorite;

        const previousList = [...favouriteMaterials.list];
        const previousTotal = favouriteMaterials.total;

        const schoolId = viaSchoolWishlist
            ? getState().main.user.profileDetails.details.schoolAccount?.school_id ?? null
            : null;

        trackFavoriteChange({ material, materialIndex, add, viaSchoolWishlist, schoolId, searchRequestId });

        if (add) {
            favouriteMaterials.list.unshift(material);
            favouriteMaterials.total += 1;
        } else {
            favouriteMaterials.list.forEach((item, index) => {
                if (material?.id && item?.id?.toString() === material?.id?.toString()) {
                    favouriteMaterials.list.splice(index, 1);
                }
            });
            favouriteMaterials.total -= 1;
        }

        dispatch({
            list: favouriteMaterials.list,
            total: favouriteMaterials.total,
            type: FAVORITE_GET
        });

        return Promise.resolve(request)
            .catch((error) => {
                favouriteMaterials.list = previousList;
                favouriteMaterials.total = previousTotal;

                dispatch({
                    list: previousList,
                    total: previousTotal,
                    type: FAVORITE_GET
                });

                console.error(error);
            })
            .finally(() => {
                invalidateSliderCaches();
            });
    };
